import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Dialog, DialogContent,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, X, Download } from "lucide-react";
import Markdown from "@/components/Markdown";

function isHtmlish(s) {
  return typeof s === "string" && /<(p|ul|ol|strong|em|h[1-4]|br|li|blockquote)\b/i.test(s);
}

function RichKV({ label, value }) {
  if (!value) return null;
  return (
    <div className="brand-print-kv">
      <div className="brand-print-kv-label">{label}</div>
      {isHtmlish(value)
        ? <div className="brand-print-kv-value brand-md" dangerouslySetInnerHTML={{ __html: value }} />
        : /\*\*|^-\s|^\d+\.\s/m.test(value)
          ? <div className="brand-print-kv-value"><Markdown>{value}</Markdown></div>
          : <div className="brand-print-kv-value">{value}</div>}
    </div>
  );
}

/** Iter 172 · Brand full-detail print preview + PDF export.
 *  Uses the browser's print dialog with a scoped print stylesheet so users
 *  can "Save as PDF" from any modern browser (Chrome/Edge/Firefox/Safari). */

function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(); } catch { return v; }
}

function Section({ title, children, empty }) {
  return (
    <section className="brand-print-section">
      <h2 className="brand-print-h2">{title}</h2>
      {empty ? <p className="brand-print-muted">—</p> : children}
    </section>
  );
}

function KV({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="brand-print-kv">
      <div className="brand-print-kv-label">{label}</div>
      <div className="brand-print-kv-value">
        {typeof value === "string" ? value : JSON.stringify(value)}
      </div>
    </div>
  );
}

function List({ items, render }) {
  if (!Array.isArray(items) || items.length === 0)
    return <p className="brand-print-muted">—</p>;
  return (
    <ul className="brand-print-list">
      {items.map((it, i) => <li key={i}>{render ? render(it) : String(it)}</li>)}
    </ul>
  );
}

function Table({ rows, cols }) {
  if (!Array.isArray(rows) || rows.length === 0)
    return <p className="brand-print-muted">—</p>;
  return (
    <table className="brand-print-table">
      <thead>
        <tr>{cols.map((c) => <th key={c.key}>{c.label}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {cols.map((c) => <td key={c.key}>{c.render ? c.render(r) : (r[c.key] || "—")}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function BrandPrintPreview({ brandId, onClose }) {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get(`/brands/${brandId}/full-detail`)
       .then((r) => setD(r.data))
       .catch((e) => { toast.error(apiErrorMessage(e, "Failed to load brand")); onClose?.(); });
  }, [brandId, onClose]);

  const doPrint = () => window.print();

  if (!d) {
    return (
      <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent className="max-w-3xl">
          <div className="p-8 text-center text-muted-foreground">Preparing preview…</div>
        </DialogContent>
      </Dialog>
    );
  }

  const b = d.brand || {};
  const core = b.core || {};
  const strategy = b.strategy || {};
  const swot = b.swot || {};
  const voice = b.voice || {};
  const visual = b.visual || {};
  const digital = b.digital || {};
  const ops = b.ops || {};
  const health = d.health || {};

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-[900px] w-[92vw] max-h-[92vh] overflow-hidden flex flex-col p-0"
        data-testid="brand-print-preview-dialog"
      >
        {/* Print-only stylesheet — hides the app chrome, prints only the sheet */}
        <style>{`
          @media print {
            body * { visibility: hidden !important; }
            .brand-print-sheet, .brand-print-sheet * { visibility: visible !important; }
            .brand-print-sheet {
              position: absolute; top: 0; left: 0; width: 100%;
              padding: 24px 32px !important;
              background: white !important;
            }
            .brand-print-noprint { display: none !important; }
            .brand-print-section { page-break-inside: avoid; break-inside: avoid; }
          }
          .brand-print-sheet { color: #111; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
          .brand-print-h1 { font-size: 26px; font-weight: 700; margin: 0; }
          .brand-print-h2 { font-size: 14px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #0f172a; margin: 20px 0 8px; padding-bottom: 4px; border-bottom: 1px solid #cbd5e1; }
          .brand-print-h3 { font-size: 12px; font-weight: 700; margin: 12px 0 4px; color: #334155; }
          .brand-print-muted { color: #94a3b8; font-size: 12px; }
          .brand-print-eyebrow { text-transform: uppercase; letter-spacing: 0.08em; font-size: 10px; color: #64748b; font-weight: 600; }
          .brand-print-list { padding-left: 18px; margin: 4px 0; font-size: 12px; }
          .brand-print-list li { margin-bottom: 4px; }
          .brand-print-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
          .brand-print-kv { font-size: 12px; }
          .brand-print-kv-label { font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; }
          .brand-print-kv-value { color: #0f172a; }
          .brand-print-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 4px; }
          .brand-print-table th, .brand-print-table td { border: 1px solid #e2e8f0; padding: 6px 8px; text-align: left; vertical-align: top; }
          .brand-print-table th { background: #f1f5f9; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
          .brand-print-section { margin-bottom: 12px; }
          .brand-print-badge { display: inline-block; padding: 2px 8px; border: 1px solid #cbd5e1; font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600; }
          .brand-print-health-bar { height: 8px; background: #e2e8f0; border-radius: 4px; overflow: hidden; margin-top: 4px; }
          .brand-print-health-fill { height: 100%; background: linear-gradient(90deg, #10b981, #0ea5e9); }
          .brand-print-para { font-size: 12px; line-height: 1.5; white-space: pre-wrap; }
        `}</style>

        <div className="border-b border-border px-5 py-3 flex items-center gap-2 brand-print-noprint">
          <span className="text-sm font-semibold">Print preview — {b.name}</span>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" className="rounded-none h-8" onClick={doPrint}
                      data-testid="brand-print-btn">
              <Printer className="w-3.5 h-3.5 mr-1.5" /> Print / Save as PDF
            </Button>
            <Button variant="outline" className="rounded-none h-8"
                      onClick={() => window.open(`/app/brands/${b.id}?print=1`, "_blank")}
                      data-testid="brand-print-open-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Open standalone
            </Button>
            <Button variant="ghost" className="rounded-none h-8 w-8 p-0" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-muted/30 py-6">
          <div className="brand-print-sheet bg-white mx-auto shadow-sm border border-border p-8"
                 style={{ width: "min(900px, 96%)" }}
                 data-testid="brand-print-sheet">
            <div className="flex items-start gap-4">
              {b.logo_url && (
                <img src={b.logo_url} alt="" style={{ width: 64, height: 64, objectFit: "contain", border: "1px solid #cbd5e1" }} />
              )}
              <div className="flex-1">
                <div className="brand-print-eyebrow">Brand book</div>
                <h1 className="brand-print-h1">{b.name}</h1>
                <div className="text-xs text-muted-foreground mt-1">
                  {[b.industry, b.sub_industry, b.website, b.client_name].filter(Boolean).join(" · ") || "—"}
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className="brand-print-badge">{b.status || "active"}</span>
                  <span className="brand-print-badge">Health {health.score || 0}/100 · {health.band || "n/a"}</span>
                </div>
                <div className="brand-print-health-bar">
                  <div className="brand-print-health-fill" style={{ width: `${health.score || 0}%` }} />
                </div>
              </div>
              <div className="text-right">
                <div className="brand-print-eyebrow">Exported</div>
                <div className="text-xs">{fmtDate(d.exported_at)}</div>
              </div>
            </div>

            <Section title="Core identity" empty={!core.mission && !core.vision && !core.positioning}>
              <div className="brand-print-grid">
                <RichKV label="Tagline" value={core.tagline} />
                <RichKV label="Elevator pitch" value={core.elevator_pitch} />
                <RichKV label="Mission" value={core.mission} />
                <RichKV label="Vision" value={core.vision} />
                <RichKV label="Positioning" value={core.positioning} />
                <RichKV label="Story" value={core.story} />
              </div>
              {core.values?.length > 0 && (
                <>
                  <h3 className="brand-print-h3">Core values</h3>
                  <List items={core.values} />
                </>
              )}
            </Section>

            <Section title="Strategy" empty={!strategy.target_market && !(strategy.objectives?.length) && !(strategy.personas?.length)}>
              <KV label="Target market" value={strategy.target_market} />
              {strategy.objectives?.length > 0 && (
                <>
                  <h3 className="brand-print-h3">Objectives</h3>
                  <List items={strategy.objectives} />
                </>
              )}
              {strategy.personas?.length > 0 && (
                <>
                  <h3 className="brand-print-h3">Personas</h3>
                  <Table rows={strategy.personas} cols={[
                    { key: "name", label: "Name" },
                    { key: "demographics", label: "Demographics" },
                    { key: "psychographics", label: "Psychographics" },
                    { key: "pain_points", label: "Pain points" },
                    { key: "channels", label: "Channels" },
                  ]} />
                </>
              )}
            </Section>

            <Section title="USPs" empty={!(b.usps?.length)}>
              <List items={b.usps || []}
                      render={(u) => typeof u === "string" ? u
                        : <><b>{u.title || ""}</b>{u.detail ? ` — ${u.detail}` : ""}</>} />
            </Section>

            <Section title="SWOT" empty={!(swot.strengths?.length || swot.weaknesses?.length || swot.opportunities?.length || swot.threats?.length)}>
              <div className="brand-print-grid">
                <div><h3 className="brand-print-h3">Strengths</h3><List items={swot.strengths} /></div>
                <div><h3 className="brand-print-h3">Weaknesses</h3><List items={swot.weaknesses} /></div>
                <div><h3 className="brand-print-h3">Opportunities</h3><List items={swot.opportunities} /></div>
                <div><h3 className="brand-print-h3">Threats</h3><List items={swot.threats} /></div>
              </div>
            </Section>

            <Section title="Voice" empty={!(voice.attributes?.length || voice.dos?.length || voice.donts?.length)}>
              <div className="brand-print-grid">
                <div><h3 className="brand-print-h3">Attributes</h3><List items={voice.attributes} /></div>
                <div>
                  <h3 className="brand-print-h3">Do&apos;s</h3><List items={voice.dos} />
                  <h3 className="brand-print-h3" style={{ marginTop: 8 }}>Don&apos;ts</h3><List items={voice.donts} />
                </div>
              </div>
              {voice.key_messages?.length > 0 && (
                <>
                  <h3 className="brand-print-h3">Key messages</h3>
                  <List items={voice.key_messages} />
                </>
              )}
              {voice.boilerplates?.length > 0 && (
                <>
                  <h3 className="brand-print-h3">Boilerplates</h3>
                  <List items={voice.boilerplates} />
                </>
              )}
            </Section>

            <Section title="Visual" empty={!(visual.colors?.length || visual.typography?.length || visual.logo_rules)}>
              <KV label="Logo rules" value={visual.logo_rules} />
              <KV label="Imagery style" value={visual.imagery_style} />
              {visual.colors?.length > 0 && (
                <>
                  <h3 className="brand-print-h3">Colors</h3>
                  <List items={visual.colors}
                          render={(c) => typeof c === "string" ? c
                            : `${c.name || c.role || ""} — ${c.hex || c.value || ""}`} />
                </>
              )}
              {visual.typography?.length > 0 && (
                <>
                  <h3 className="brand-print-h3">Typography</h3>
                  <List items={visual.typography} />
                </>
              )}
            </Section>

            <Section title="Digital" empty={!(digital.socials?.length || digital.seo_keywords?.length)}>
              {digital.socials?.length > 0 && (
                <Table rows={digital.socials} cols={[
                  { key: "platform", label: "Platform" },
                  { key: "handle", label: "Handle" },
                  { key: "url", label: "URL" },
                ]} />
              )}
              {digital.seo_keywords?.length > 0 && (
                <>
                  <h3 className="brand-print-h3">SEO keywords</h3>
                  <p className="brand-print-para">{digital.seo_keywords.join(" · ")}</p>
                </>
              )}
              {digital.hashtag_banks?.length > 0 && (
                <>
                  <h3 className="brand-print-h3">Hashtag banks</h3>
                  <List items={digital.hashtag_banks} />
                </>
              )}
              {digital.cta_bank?.length > 0 && (
                <>
                  <h3 className="brand-print-h3">CTA bank</h3>
                  <List items={digital.cta_bank} />
                </>
              )}
              <KV label="Compliance notes" value={digital.compliance_notes} />
            </Section>

            <Section title="Content pillars" empty={!(b.pillars?.length)}>
              <Table rows={b.pillars || []} cols={[
                { key: "name", label: "Pillar" },
                { key: "target_pct", label: "Target %" },
                { key: "description", label: "Description" },
              ]} />
            </Section>

            <Section title="Competitors" empty={!(b.competitors?.length)}>
              {(b.competitors || []).length > 0 && (
                <div className="space-y-3">
                  {(b.competitors || []).map((cp, i) => (
                    <div key={i} className="border border-border p-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <b>{cp.name || "—"}</b>
                        {cp.website && (
                          <a href={cp.website} target="_blank" rel="noreferrer"
                                className="text-primary text-xs">{cp.website.replace(/^https?:\/\//, "").slice(0, 40)}</a>
                        )}
                        {cp.threat_level && (
                          <span className="brand-print-badge">Threat: {cp.threat_level}</span>
                        )}
                        {cp.pricing_model && (
                          <span className="brand-print-badge">{cp.pricing_model}{cp.pricing_range ? " · " + cp.pricing_range : ""}</span>
                        )}
                      </div>
                      {cp.positioning && (
                        <div className="mt-2">
                          <div className="brand-print-eyebrow">Positioning</div>
                          <Markdown>{cp.positioning}</Markdown>
                        </div>
                      )}
                      {cp.elevator_pitch && (
                        <div className="mt-2">
                          <div className="brand-print-eyebrow">Elevator pitch</div>
                          <Markdown>{cp.elevator_pitch}</Markdown>
                        </div>
                      )}
                      <div className="brand-print-grid mt-2">
                        {cp.strengths?.length > 0 && (
                          <div>
                            <div className="brand-print-eyebrow">Strengths</div>
                            <List items={cp.strengths} />
                          </div>
                        )}
                        {cp.weaknesses?.length > 0 && (
                          <div>
                            <div className="brand-print-eyebrow">Weaknesses</div>
                            <List items={cp.weaknesses} />
                          </div>
                        )}
                        {cp.our_advantage && (
                          <div>
                            <div className="brand-print-eyebrow">Our advantage</div>
                            <Markdown>{cp.our_advantage}</Markdown>
                          </div>
                        )}
                        {cp.their_advantage && (
                          <div>
                            <div className="brand-print-eyebrow">Their advantage</div>
                            <Markdown>{cp.their_advantage}</Markdown>
                          </div>
                        )}
                      </div>
                      {cp.notes && (
                        <div className="mt-2">
                          <div className="brand-print-eyebrow">Notes</div>
                          <Markdown>{cp.notes}</Markdown>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section title="Ops" empty={!(ops.slas || ops.timezone || (ops.approval_contacts?.length))}>
              <KV label="Timezone" value={ops.timezone} />
              <KV label="Review windows" value={ops.review_windows} />
              <KV label="SLAs" value={ops.slas} />
              {ops.approval_contacts?.length > 0 && (
                <>
                  <h3 className="brand-print-h3">Approval contacts</h3>
                  <List items={ops.approval_contacts}
                          render={(c) => typeof c === "string" ? c
                            : `${c.name || ""}${c.email ? " · " + c.email : ""}${c.role ? " · " + c.role : ""}`} />
                </>
              )}
              {ops.escalation?.length > 0 && (
                <>
                  <h3 className="brand-print-h3">Escalation path</h3>
                  <List items={ops.escalation} />
                </>
              )}
            </Section>

            <Section title="Important dates" empty={!(b.important_dates?.length)}>
              <Table rows={b.important_dates || []} cols={[
                { key: "title", label: "Title" },
                { key: "date", label: "Date" },
                { key: "notes", label: "Notes" },
              ]} />
            </Section>

            <Section title="Contacts" empty={!(d.contacts?.length)}>
              <Table rows={d.contacts || []} cols={[
                { key: "name", label: "Name" },
                { key: "role", label: "Role" },
                { key: "email", label: "Email" },
                { key: "phone", label: "Phone" },
              ]} />
            </Section>

            <Section title="Projects" empty={!(d.projects?.length)}>
              <Table rows={d.projects || []} cols={[
                { key: "name", label: "Project" },
                { key: "status", label: "Status" },
                { key: "start_date", label: "Start", render: (r) => fmtDate(r.start_date) },
                { key: "end_date", label: "End", render: (r) => fmtDate(r.end_date) },
              ]} />
            </Section>

            <Section title="Packages" empty={!(d.packages?.length)}>
              <Table rows={d.packages || []} cols={[
                { key: "name", label: "Package" },
                { key: "status", label: "Status" },
                { key: "monthly_price", label: "Monthly ₹" },
                { key: "start_date", label: "Start", render: (r) => fmtDate(r.start_date) },
                { key: "end_date", label: "End", render: (r) => fmtDate(r.end_date) },
              ]} />
            </Section>

            <Section title="Recent activity (last 25)" empty={!(d.activity?.length)}>
              <Table rows={d.activity || []} cols={[
                { key: "created_at", label: "When", render: (r) => fmtDate(r.created_at) },
                { key: "actor_name", label: "Who" },
                { key: "action", label: "Action" },
              ]} />
            </Section>

            <div className="mt-6 text-center brand-print-muted" style={{ fontSize: 10 }}>
              Generated by Acquilix · {fmtDate(d.exported_at)}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
